import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatCompact, formatDate, formatMoney, formatMonth, formatPeriod } from "@/lib/format";
import { isCalendarMonth } from "@/lib/period";
import { ChartFrame, DataTable, TooltipCard } from "./ChartFrame";

const AXIS = { fill: "var(--axis)", fontSize: 11 };
const GRID = "var(--grid)";
const SERIES_1 = "var(--series-1)";
const SERIES_2 = "var(--series-2)";
const PLOT_HEIGHT = 220; // includes the x-axis band: no nested scroll

interface TooltipProps {
  active?: boolean;
  payload?: readonly { payload?: unknown }[];
}

function hovered(props: TooltipProps): Record<string, unknown> | null {
  const p = props.active ? props.payload?.[0]?.payload : undefined;
  return p && typeof p === "object" ? (p as Record<string, unknown>) : null;
}

// --- Income vs expense, by month (two series, grouped columns) -----------------------------

export interface MonthPoint {
  month: string;
  period_start: string;
  period_end: string;
  income: string;
  expense: string;
  net?: string;
}

/**
 * A row's `month` field (the calendar month its period starts in) is unique per row and stable,
 * so it doubles as the chart's data key; this looks the full period back up from it for a label -
 * recharts' own tickFormatter only ever hands back that one axis value, never the whole row.
 */
function periodLabeller(data: readonly { month: string; period_start: string; period_end: string }[]) {
  const byMonth = new Map(data.map((d) => [d.month, d]));
  return (month: string, short = false): string => {
    const row = byMonth.get(month);
    return row ? formatPeriod(row.period_start, row.period_end, isCalendarMonth({ start: row.period_start, end: row.period_end }), short) : formatMonth(month, short);
  };
}

export function IncomeExpenseChart({ data, currency, title = "Income vs expenses", loading }: {
  data: MonthPoint[];
  currency: string;
  title?: string;
  loading?: boolean;
}) {
  const rows = data.map((d) => ({ ...d, incomeN: Number(d.income), expenseN: Number(d.expense) }));
  const label = periodLabeller(data);
  return (
    <ChartFrame
      title={title}
      subtitle="Transfers between your accounts are excluded"
      loading={loading}
      legend={[
        { label: "Income", color: SERIES_1 },
        { label: "Expenses", color: SERIES_2 },
      ]}
      table={
        <DataTable
          columns={[
            { key: "month", label: "Month" },
            { key: "income", label: "Income", align: "right" },
            { key: "expense", label: "Expenses", align: "right" },
            { key: "net", label: "Net", align: "right" },
          ]}
          rows={data.map((d) => ({
            month: label(d.month),
            income: formatMoney(d.income, currency),
            expense: formatMoney(d.expense, currency),
            net: d.net !== undefined ? formatMoney(d.net, currency) : "",
          }))}
        />
      }
    >
      <div style={{ height: PLOT_HEIGHT }} role="img"
           aria-label={`${title}: monthly income and expenses. Use the Table button for values.`}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} barGap={2} barCategoryGap="28%" margin={{ top: 8, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={GRID} />
            <XAxis dataKey="month" tickFormatter={(m: string) => label(m, true)} tick={AXIS}
                   axisLine={{ stroke: "var(--baseline)" }} tickLine={false} interval="preserveStartEnd" />
            <YAxis tickFormatter={(v: number) => formatCompact(v)} tick={AXIS} axisLine={false}
                   tickLine={false} width={44} />
            <Tooltip
              cursor={{ fill: "var(--surface-2)" }}
              content={(props: TooltipProps) => {
                const p = hovered(props);
                if (!p) return null;
                return (
                  <TooltipCard
                    title={label(String(p.month))}
                    rows={[
                      { label: "Income", value: formatMoney(String(p.income), currency), color: SERIES_1 },
                      { label: "Expenses", value: formatMoney(String(p.expense), currency), color: SERIES_2 },
                    ]}
                  />
                );
              }}
            />
            <Bar dataKey="incomeN" name="Income" fill={SERIES_1} maxBarSize={24} radius={[4, 4, 0, 0]} isAnimationActive={false} />
            <Bar dataKey="expenseN" name="Expenses" fill={SERIES_2} maxBarSize={24} radius={[4, 4, 0, 0]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartFrame>
  );
}

// --- Net cash flow, by month (diverging around zero) ---------------------------------------

interface BarShapeProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  value?: number;
}

/** 4px rounded data-end, square at the baseline — for bars on either side of zero. */
function DivergingBar({ x = 0, y = 0, width = 0, height = 0, fill, value = 0 }: BarShapeProps) {
  const top = Math.min(y, y + height);
  const h = Math.abs(height);
  if (h === 0 || width === 0) return null;
  const r = Math.min(4, h, width / 2);
  const positive = value >= 0;
  const d = positive
    ? `M${x},${top + h} V${top + r} Q${x},${top} ${x + r},${top} H${x + width - r} Q${x + width},${top} ${x + width},${top + r} V${top + h} Z`
    : `M${x},${top} V${top + h - r} Q${x},${top + h} ${x + r},${top + h} H${x + width - r} Q${x + width},${top + h} ${x + width},${top + h - r} V${top} Z`;
  return <path d={d} fill={fill} />;
}

export function NetCashFlowChart({ data, currency, loading }: {
  data: { month: string; period_start: string; period_end: string; inflow: string; outflow: string; net: string }[];
  currency: string;
  loading?: boolean;
}) {
  const rows = data.map((d) => ({ ...d, netN: Number(d.net) }));
  const label = periodLabeller(data);
  return (
    <ChartFrame
      title="Net cash flow"
      subtitle="Money in minus money out each month, excluding transfers"
      loading={loading}
      legend={[
        { label: "Surplus", color: "var(--diverge-pos)" },
        { label: "Deficit", color: "var(--diverge-neg)" },
      ]}
      table={
        <DataTable
          columns={[
            { key: "month", label: "Month" },
            { key: "inflow", label: "In", align: "right" },
            { key: "outflow", label: "Out", align: "right" },
            { key: "net", label: "Net", align: "right" },
          ]}
          rows={data.map((d) => ({
            month: label(d.month),
            inflow: formatMoney(d.inflow, currency),
            outflow: formatMoney(d.outflow, currency),
            net: formatMoney(d.net, currency, { signed: true }),
          }))}
        />
      }
    >
      <div style={{ height: PLOT_HEIGHT }} role="img" aria-label="Net cash flow by month. Use the Table button for values.">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} barCategoryGap="30%" margin={{ top: 8, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={GRID} />
            <XAxis dataKey="month" tickFormatter={(m: string) => label(m, true)} tick={AXIS}
                   axisLine={false} tickLine={false} interval="preserveStartEnd" />
            <YAxis tickFormatter={(v: number) => formatCompact(v)} tick={AXIS} axisLine={false}
                   tickLine={false} width={44} />
            <ReferenceLine y={0} stroke="var(--baseline)" />
            <Tooltip
              cursor={{ fill: "var(--surface-2)" }}
              content={(props: TooltipProps) => {
                const p = hovered(props);
                if (!p) return null;
                const net = Number(p.net);
                return (
                  <TooltipCard
                    title={label(String(p.month))}
                    rows={[
                      { label: net >= 0 ? "Surplus" : "Deficit", value: formatMoney(String(p.net), currency, { signed: true }),
                        color: net >= 0 ? "var(--diverge-pos)" : "var(--diverge-neg)" },
                      { label: "In", value: formatMoney(String(p.inflow), currency), color: "transparent" },
                      { label: "Out", value: formatMoney(String(p.outflow), currency), color: "transparent" },
                    ]}
                  />
                );
              }}
            />
            <Bar dataKey="netN" maxBarSize={24} isAnimationActive={false}
                 shape={(props: unknown) => <DivergingBar {...(props as BarShapeProps)} />}>
              {rows.map((r) => (
                <Cell key={r.month} fill={r.netN >= 0 ? "var(--diverge-pos)" : "var(--diverge-neg)"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartFrame>
  );
}

// --- A single balance over time (line + 10% wash, crosshair) --------------------------------

export function BalanceLineChart({ title, subtitle, data, currency, dateKind = "day", loading }: {
  title: string;
  subtitle?: string;
  /** `period_start` is only needed (and only read) when dateKind is "month": `date` is then the
   * financial period's own last day, e.g. 25 Sep for a 26th-start cycle - not always month-end. */
  data: { date: string; period_start?: string; value: string }[];
  currency: string;
  dateKind?: "day" | "month";
  loading?: boolean;
}) {
  const rows = data.map((d) => ({ ...d, v: Number(d.value) }));
  const monthLabel = (d: { date: string; period_start?: string }, short: boolean) =>
    formatPeriod(d.period_start ?? d.date, d.date, isCalendarMonth({ start: d.period_start ?? d.date, end: d.date }), short);
  const byDate = new Map(data.map((d) => [d.date, d]));
  const fmt = (date: string, short: boolean) => {
    if (dateKind !== "month") return formatDate(date, short);
    const d = byDate.get(date);
    return d ? monthLabel(d, short) : formatMonth(date.slice(0, 7), short);
  };
  const last = rows[rows.length - 1];
  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      loading={loading}
      table={
        <DataTable
          columns={[{ key: "date", label: "Date" }, { key: "value", label: "Balance", align: "right" }]}
          rows={data.map((d) => ({
            date: dateKind === "month" ? monthLabel(d, false) : formatDate(d.date),
            value: formatMoney(d.value, currency),
          }))}
        />
      }
    >
      {last && (
        <p className="mb-1 text-xs text-ink-3">
          Latest <span className="font-semibold text-ink">{formatMoney(last.value, currency)}</span>
        </p>
      )}
      <div style={{ height: PLOT_HEIGHT }} role="img" aria-label={`${title}. Use the Table button for values.`}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={GRID} />
            <XAxis dataKey="date" tickFormatter={(d: string) => fmt(d, true)} tick={AXIS}
                   axisLine={{ stroke: "var(--baseline)" }} tickLine={false} minTickGap={24} />
            <YAxis tickFormatter={(v: number) => formatCompact(v)} tick={AXIS} axisLine={false}
                   tickLine={false} width={44} domain={["auto", "auto"]} />
            <Tooltip
              cursor={{ stroke: "var(--baseline)", strokeWidth: 1 }}
              content={(props: TooltipProps) => {
                const p = hovered(props);
                if (!p) return null;
                return (
                  <TooltipCard
                    title={fmt(String(p.date), false)}
                    rows={[{ label: "Balance", value: formatMoney(String(p.value), currency), color: SERIES_1 }]}
                  />
                );
              }}
            />
            <Area
              type="monotone"
              dataKey="v"
              stroke={SERIES_1}
              strokeWidth={2}
              fill={SERIES_1}
              fillOpacity={0.1}
              isAnimationActive={false}
              dot={false}
              activeDot={{ r: 4, fill: SERIES_1, stroke: "var(--surface)", strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </ChartFrame>
  );
}
